import { useDisclosure } from '@chakra-ui/react'
import { Dispatch, FC, SetStateAction, useState } from 'react'

import DialogWindow from '@/components/DialogWindow'
import { Button, Typography } from '@/components/ui'
import CustomInput from '@/components/ui/input/CustomInput'

import Cancel from '@/assets/icons/cancel-3.svg'
import Check from '@/assets/icons/check.svg'
import CircleDashed from '@/assets/icons/circle-dashed.svg'
import DownloadSimple from '@/assets/icons/download-simple.svg'
import Link from '@/assets/icons/link.svg'
import Plus2 from '@/assets/icons/plus2.svg'
import Trash from '@/assets/icons/trash.svg'

import { IHighlightsFolder } from '@/types/highlight.interface'
import { formatDate } from '@/utils/formatDate'
import { getFolderNameError } from '@/utils/validateFolderName'

import KeywordSearchInput from './KeywordSearchInput'
import TimeInput from './TimeInput'

interface IProps {
	timeStart: string
	timeEnd: string
	setTimeStart: (value: string) => void
	setTimeEnd: (value: string) => void
	isLoading: boolean
	isSelectMode: boolean
	toggleSelectedMode: Dispatch<SetStateAction<boolean>>
	createFolder: ({
		taskId,
		name
	}: {
		taskId: string
		name: string
	}) => Promise<unknown> | unknown
	taskId: string
	onCancelSelection: () => void
	deleteSelectedFolders: () => void
	disabled: boolean
	onAddManualSegment: (position: { x: number; y: number }) => void
	downloadSelectedFolders: () => void
	folders: IHighlightsFolder[]
	shareSelectedFolders: () => void
	isShareLinkCreating: boolean
	keywords: string[]
	isKeywordSearching: boolean
	onAddKeyword: (keyword: string) => void
	onRemoveKeyword: (keyword: string) => void
}

const HighlightControls: FC<IProps> = ({
	timeEnd,
	setTimeEnd,
	setTimeStart,
	timeStart,
	isLoading,
	isSelectMode,
	toggleSelectedMode,
	createFolder,
	taskId,
	onCancelSelection,
	deleteSelectedFolders,
	disabled,
	onAddManualSegment,
	downloadSelectedFolders,
	folders,
	shareSelectedFolders,
	isShareLinkCreating,
	keywords,
	isKeywordSearching,
	onAddKeyword,
	onRemoveKeyword
}) => {
	const [folderName, setFolderName] = useState('')
	const [folderNameError, setFolderNameError] = useState('')

	const createFolderModal = useDisclosure()

	const openCreateFolder = () => {
		setFolderName('')
		createFolderModal.onOpen()
	}

	const closeCreateFolder = () => {
		createFolderModal.onClose()
		setFolderName('')
		setFolderNameError('')
	}

	const saveCreatedFolder = async () => {
		if (!taskId) return

		const error = getFolderNameError(folderName, folders)

		if (error) {
			setFolderNameError(error)
			return
		}

		const name = folderName.trim() || formatDate(new Date().toUTCString(), true)

		closeCreateFolder()

		try {
			await createFolder({
				taskId,
				name
			})
		} catch (error) {
			console.log(error)
		}
	}

	const isValidTime = () => {
		const isValidFormat = (value: string) =>
			value.length === 8 &&
			value[2] === ':' &&
			value[5] === ':' &&
			[0, 1, 3, 4, 6, 7].every(i => /\d/.test(value[i]))

		if (!isValidFormat(timeStart) || !isValidFormat(timeEnd)) {
			return false
		}

		const parseTime = (str: string) => {
			const parts = str.split(':').map(Number)
			return parts[0] * 3600 + parts[1] * 60 + parts[2]
		}

		const startInSeconds = parseTime(timeStart)
		const endInSeconds = parseTime(timeEnd)

		return endInSeconds > startInSeconds
	}

	const addManualSegment = (
		event: React.MouseEvent<HTMLButtonElement, MouseEvent>
	) => {
		const rect = event.currentTarget.getBoundingClientRect()

		onAddManualSegment({
			x: rect.left + rect.width / 2,
			y: rect.bottom + 8
		})
	}

	return (
		<>
			<div className='border-b border-neutral-100 flex items-center gap-2 py-2.5 mx-3'>
				<TimeInput
					value={timeStart}
					onChange={setTimeStart}
					disabled={isLoading}
					placeholder='Start'
					className='!rounded-[8px] hover:bg-neutral-300 focus:bg-neutral-300'
				/>

				{'-'}

				<TimeInput
					value={timeEnd}
					onChange={setTimeEnd}
					disabled={isLoading}
					placeholder='End'
					className='!rounded-[8px] hover:bg-neutral-300 focus:bg-neutral-300'
				/>
				<Button
					className='py-2 px-3 bg-blue-400/[0.15] !text-blue-600 rounded-[8px] hover:bg-blue-400/[0.5]'
					disabled={!isValidTime() || isLoading}
					onClick={addManualSegment}
				>
					Add
				</Button>
			</div>

			<div className='border-b border-neutral-100 flex items-center py-2.5 mx-3'>
				<KeywordSearchInput
					keywords={keywords}
					isLoading={isLoading || isKeywordSearching}
					onAddKeyword={onAddKeyword}
					onRemoveKeyword={onRemoveKeyword}
				/>
			</div>

			<div className='flex justify-between items-center gap-2.5 py-2.5 mx-3'>
				{isSelectMode ? (
					<>
						<Button
							className='w-full py-[9px] px-3 !text-neutral-900 bg-neutral-200 hover:bg-neutral-300 rounded-[8px]'
							classNameText='flex gap-2 items-center'
							onClick={onCancelSelection}
						>
							<Cancel />
							<span>Cancel</span>
						</Button>
						<Button
							disabled={disabled || isShareLinkCreating}
							className='p-2 bg-neutral-200 hover:bg-neutral-300 rounded-full'
							onClick={shareSelectedFolders}
						>
							<Link className='text-neutral-900 w-4 h-4' />
						</Button>
						<Button
							disabled={disabled}
							className='p-2 bg-neutral-200 hover:bg-neutral-300 rounded-full'
							onClick={downloadSelectedFolders}
						>
							<DownloadSimple className='text-neutral-900 w-4 h-4' />
						</Button>
						<Button
							disabled={disabled}
							className='p-2 bg-neutral-200 hover:bg-neutral-300 rounded-full'
							onClick={deleteSelectedFolders}
						>
							<Trash className='text-neutral-900 w-4 h-4' />
						</Button>
					</>
				) : (
					<>
						<Button
							className='w-full py-[9px] px-3 !text-neutral-900 bg-neutral-200 hover:bg-neutral-300 rounded-[8px]'
							classNameText='flex gap-2 items-center'
							onClick={openCreateFolder}
						>
							<Plus2 />
							<span>New folder</span>
						</Button>
						<Button
							className='w-full py-[9px] px-3 !text-neutral-900 bg-neutral-200 hover:bg-neutral-300 rounded-[8px]'
							classNameText='flex gap-2 items-center'
							disabled={!folders.length}
							onClick={() => toggleSelectedMode(true)}
						>
							<CircleDashed />
							<span className='flex'>Select</span>
						</Button>
					</>
				)}
			</div>

			<DialogWindow
				handleClick={saveCreatedFolder}
				open={createFolderModal.open}
				onClose={closeCreateFolder}
				titleText='Create new folder'
				body={
					<div className='flex flex-col gap-2'>
						<Typography
							variant='text'
							className='!text-[16px] leading-[20px] text-black'
						>
							Folder name
						</Typography>
						<CustomInput
							value={folderName}
							onChange={e => {
								setFolderName(e.target.value)
								setFolderNameError('')
							}}
							placeholder='Add name folder'
							maxLength={50}
							errorMessage={folderNameError}
						/>
					</div>
				}
				bodyClassname='flex flex-col gap-4'
				noButtonText={
					<>
						<Cancel className='w-3 h-3' />
						<span>Cancel</span>
					</>
				}
				yesButtonText={
					<>
						<Check />
						<span>Create folder</span>
					</>
				}
				noButtonClassname='!text-[16px] hover:bg-neutral-200'
				noButtonClassnameText='flex items-center gap-3'
				yesButtonClassname='bg-neutral-900 hover:bg-neutral-700'
				yesButtonClassnameText='flex items-center gap-3'
			/>
		</>
	)
}

export default HighlightControls
